import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LINKS } from "@/i18n/translations";
import { StoreBadges } from "./StoreBadges";

const FakeQr = () => (
  <svg viewBox="0 0 120 120" className="w-full h-full" aria-hidden="true">
    <rect width="120" height="120" rx="8" fill="#ffffff" />
    <g fill="#101013">
      <rect x="10" y="10" width="28" height="28" rx="3" />
      <rect x="16" y="16" width="16" height="16" fill="#fff" rx="2" />
      <rect x="20" y="20" width="8" height="8" />
      <rect x="82" y="10" width="28" height="28" rx="3" />
      <rect x="88" y="16" width="16" height="16" fill="#fff" rx="2" />
      <rect x="92" y="20" width="8" height="8" />
      <rect x="10" y="82" width="28" height="28" rx="3" />
      <rect x="16" y="88" width="16" height="16" fill="#fff" rx="2" />
      <rect x="20" y="92" width="8" height="8" />
      <rect x="48" y="14" width="7" height="7" /><rect x="62" y="24" width="7" height="7" />
      <rect x="48" y="38" width="7" height="7" /><rect x="70" y="42" width="7" height="7" />
      <rect x="14" y="48" width="7" height="7" /><rect x="30" y="56" width="7" height="7" />
      <rect x="48" y="52" width="7" height="7" /><rect x="60" y="60" width="7" height="7" />
      <rect x="76" y="52" width="7" height="7" /><rect x="92" y="48" width="7" height="7" />
      <rect x="102" y="60" width="7" height="7" /><rect x="84" y="68" width="7" height="7" />
      <rect x="48" y="76" width="7" height="7" /><rect x="62" y="86" width="7" height="7" />
      <rect x="76" y="94" width="7" height="7" /><rect x="92" y="82" width="7" height="7" />
      <rect x="100" y="100" width="7" height="7" /><rect x="52" y="100" width="7" height="7" />
    </g>
  </svg>
);

const PhonePos = () => {
  const { t } = useLang();
  return (
    <div
      role="img"
      aria-label={t.hero.phoneAlt}
      className="relative w-[280px] sm:w-[310px] rounded-[42px] border border-white/15 bg-[#0d0d10] p-2.5 shadow-[0_40px_120px_-20px_rgba(245,166,35,0.25)]"
    >
      <div className="rounded-[34px] bg-[#101013] overflow-hidden border border-white/5">
        <div className="flex justify-center pt-2.5 pb-1">
          <div className="w-20 h-5 rounded-full bg-black border border-white/10" />
        </div>
        <div className="px-5 pt-3 pb-6">
          <p className="text-[11px] uppercase tracking-widest text-[#71717a]">
            {t.hero.phoneAmountLabel}
          </p>
          <p className="text-4xl font-black tracking-tighter mt-1">
            $185<span className="text-lg text-[#a1a1aa]">.00 MXN</span>
          </p>
          <div className="mx-auto mt-5 w-44 sm:w-48">
            <FakeQr />
          </div>
          <p className="text-center text-xs text-[#a1a1aa] mt-4">{t.hero.phoneShowQr}</p>
          <div className="mt-5 flex items-center justify-center gap-2 rounded-full border border-green-500/30 bg-green-500/10 py-2.5 px-4 lp-pulse">
            <CheckCircle2 size={16} className="text-green-400" />
            <span className="text-sm font-semibold text-green-400">
              {t.hero.phoneConfirmed}
            </span>
            <span className="text-xs text-green-400/60">· {t.hero.phoneSeconds}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 0.85, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 sm:pt-36 pb-16 sm:pb-24">
      <motion.div
        style={{ opacity: glowOpacity }}
        className="absolute -top-32 right-0 w-[700px] h-[700px] lp-glow pointer-events-none"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-8 items-center">
        <div>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.05 }}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full border border-[#f5a623]/30 bg-[#f5a623]/10 px-3.5 py-1.5 text-xs font-semibold text-[#f5a623]"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#f5a623] animate-pulse" />
            {t.hero.badge}
          </motion.span>

          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span className="block" {...lineAnim(i)}>
                  {i === 2 ? <span className="text-[#f5a623]">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.7 }}
            className="mt-6 max-w-xl text-base md:text-lg text-[#a1a1aa] leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <a
              href={LINKS.demo}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-demo-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#f5a623] text-[#09090b] font-bold px-7 py-3.5 text-base hover:bg-[#ffbb3d] transition-colors"
            >
              {t.hero.ctaDemo}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
            <StoreBadges idPrefix="hero" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.7 }}
            data-testid="hero-trust-line"
            className="mt-6 text-sm text-[#71717a]"
          >
            {t.hero.trust}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40, rotate: 4 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ delay: 0.45, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          style={{ y: phoneY }}
          className="flex justify-center lg:justify-end"
        >
          <div className="lp-float">
            <PhonePos />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
